// /app/frontend/src/components/frugal-v2/components/AccountRow.jsx
// Single account row with transactions (< 150 lines)

import React, { useState, useEffect, memo } from 'react';
import { Check, Pencil, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import * as api from '../services/frugalApi';

const AccountRow = memo(({ 
  account, index, type, colorClass, t, token,
  isExpanded, isEditing, editForm, 
  onToggleExpand, onStartEdit, onSetDefault,
  onEditFormChange, onUpdate, onCancelEdit, onCreate
}) => {
  const [transactions, setTransactions] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);

  useEffect(() => {
    if (isExpanded && account?.id && !isEditing) {
      setLoadingTxns(true);
      api.fetchAccountTransactions(token, account.id)
        .then(data => setTransactions(data.transactions || []))
        .catch(err => console.error('Error loading transactions:', err))
        .finally(() => setLoadingTxns(false));
    }
  }, [isExpanded, account?.id, token, isEditing]);

  if (!account) {
    return (
      <button onClick={onCreate}
        className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-dashed border-white/20 hover:border-white/40 transition-all w-full">
        <div className="w-5 h-5 rounded border border-white/20" />
        <span className="flex-1 text-slate-500 text-sm text-left">
          {type === 'bank' ? `${t('frugal.accounts.banks')} ${index}` : `${t('frugal.accounts.cards')} ${index}`}
        </span>
        <span className="text-slate-600 text-sm">$0.00</span>
      </button>
    );
  }

  return (
    <div className="bg-white/5 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-3">
        <button
          onClick={() => !account.is_default && onSetDefault(account.id)}
          title={account.is_default ? t('frugal.accounts.defaultAccount') : t('frugal.accounts.setDefault')}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
            account.is_default ? `${colorClass} border-transparent` : 'border-white/30 hover:border-white/50 hover:bg-white/10'
          }`}>
          {account.is_default && <Check className="w-3.5 h-3.5 text-white" />}
        </button>
        
        {isEditing ? (
          <div className="flex-1 flex flex-col gap-2">
            <input type="text" value={editForm.name}
              onChange={(e) => onEditFormChange('name', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-sm"
              placeholder={t('frugal.accounts.name')} />
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm">$</span>
              <input type="text" inputMode="decimal" value={editForm.balance}
                onChange={(e) => onEditFormChange('balance', e.target.value.replace(/[^0-9.-]/g, ''))}
                className="flex-1 bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white text-sm text-right"
                placeholder="0.00" />
              <button onClick={() => onUpdate(account.id)} 
                className="p-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white transition-colors">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={onCancelEdit} 
                className="p-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white transition-colors">✕</button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={() => onToggleExpand(account.id)} className="text-slate-400 hover:text-white p-1 flex-shrink-0">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <div className="flex-1 min-w-0">
              <span className="text-white text-sm block truncate">{account.name}</span>
              {account.is_default && <span className="text-xs text-emerald-400">{t('frugal.accounts.primary')}</span>}
            </div>
            <span className="text-sm font-medium text-white">
              ${account.balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <button onClick={() => onStartEdit(account)} className="text-slate-400 hover:text-white p-1">
              <Pencil className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
      
      {isExpanded && !isEditing && (
        <div className="px-3 pb-3 pt-1 border-t border-white/5">
          {loadingTxns ? (
            <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : transactions.length > 0 ? (
            <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
              {transactions.map((txn, idx) => {
                const dateStr = txn.date ? new Date(txn.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '';
                const handleReconcile = async (e) => {
                  const newVal = e.target.checked;
                  setTransactions((prev) => prev.map((x) => x.id === txn.id ? { ...x, reconciled: newVal } : x));
                  try {
                    await api.reconcileTransaction(token, { txn_type: txn.type, txn_id: txn.id, reconciled: newVal });
                  } catch (err) {
                    setTransactions((prev) => prev.map((x) => x.id === txn.id ? { ...x, reconciled: !newVal } : x));
                  }
                };
                return (
                  <div key={txn.id || idx} className="flex items-center gap-2 py-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={!!txn.reconciled}
                      onChange={handleReconcile}
                      className="w-3.5 h-3.5 accent-emerald-500 cursor-pointer flex-shrink-0"
                      title={txn.reconciled ? 'Conciliado' : 'Marcar conciliado'}
                      data-testid={`reconcile-${txn.id}`}
                    />
                    <span className="text-slate-500 w-12 flex-shrink-0">{dateStr}</span>
                    <div className="flex-1 min-w-0">
                      <span className={`block truncate ${txn.amount < 0 ? 'text-rose-400' : 'text-emerald-400'} ${txn.reconciled ? 'line-through opacity-60' : ''}`}>
                        {txn.orphan && <span title="Sin cuenta asignada - edita para reasignar" className="inline-block mr-1">⚠️</span>}
                        {txn.name}
                      </span>
                      <span className={txn.category ? 'text-slate-500' : 'text-amber-400 italic'}>
                        {txn.category || 'Sin categoria'}
                        {txn.orphan && <span className="text-amber-400 ml-1">· sin cuenta</span>}
                      </span>
                    </div>
                    <span className={`font-medium ${txn.amount < 0 ? 'text-rose-400' : 'text-emerald-400'} ${txn.reconciled ? 'opacity-60' : ''}`}>
                      {txn.amount < 0 ? '-' : '+'}${Math.abs(txn.amount).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-slate-500 text-center py-2">{t('frugal.accounts.noTransactions')}</p>
          )}
        </div>
      )}
    </div>
  );
});

AccountRow.displayName = 'AccountRow';
export default AccountRow;
